import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { Calculator } from 'lucide-react';
import { LopCrudPage } from '../../components/LopCrudPage';
import { LendingFeeExplainModal } from '../../components/LendingFeeExplainModal';
import { LopSearchableSelect } from '../../components/LopSearchableSelect';
import { getLendings, getLendingBanks, getLendingAccounts } from '../../api/lendings';
import type { Lending, LendingAccount, LendingBank } from '../../types/Lending';
import { setTenantId } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { t } from '../../i18n/t';
import './LendingsPage.css';
import { formatDateOnly, formatDateTimeIL } from '../../utils/dateTime';

// DATE / DATETIME2 ISO string -> yyyy-mm-dd. Read-only display.
function fmtDate(p: ValueFormatterParams<Lending>): string {
  return formatDateOnly(p.value);
}

// The accrual is stored, not computed on read, so its age is shown to the minute
// rather than rounded to a day - "is this today's number" is the actual question.
function fmtDateTime(p: ValueFormatterParams<Lending>): string {
  const v = p.value;
  if (!v) return '';
  return formatDateTimeIL(v);
}

// Percentages are stored as a decimal FRACTION (0.0085 = 0.85% a year) - the
// project-wide rule. The x100 belongs to the display layer, here.
function fmtPercent(p: ValueFormatterParams<Lending>): string {
  const v = p.value;
  if (v === null || v === undefined) return '';
  return `${(Number(v) * 100).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}%`;
}

function fmtMoney(p: ValueFormatterParams<Lending>): string {
  const v = p.value;
  if (v === null || v === undefined) return '';
  return Number(v).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtQty(p: ValueFormatterParams<Lending>): string {
  const v = p.value;
  if (v === null || v === undefined) return '';
  return Number(v).toLocaleString('he-IL');
}

export function LendingsPage() {
  // null = the default has not been decided yet (the bank list is still loading).
  // ''   = the user explicitly asked for every bank.
  // a number = one bank.
  // The three states are distinct on purpose: without the null the page would
  // fire an all-banks fetch on mount and immediately throw the result away.
  const [searchParams] = useSearchParams();
  const urlBank = searchParams.get('bankId');
  const urlAcc = searchParams.get('acc');
  const numOrNull = (v: string | null) => (v && Number.isFinite(Number(v)) ? Number(v) : null);
  const [bankId, setBankId] = useState<string | null>(urlBank);
  // The account filter (server-side). null = all accounts of the bank. Accounts are
  // numeric text, so the combo (numeric ids) can carry them.
  const [acc, setAcc] = useState<number | null>(() => numOrNull(urlAcc));
  const [accounts, setAccounts] = useState<LendingAccount[]>([]);
  // null = not resolved (still loading, OR the lookup failed). [] = resolved and
  // genuinely empty. The distinction is load-bearing: on failure the page falls
  // back to every bank and the grid can hold real rows, so treating an unresolved
  // list as "no lendings" would put a false banner over a populated grid — the
  // same conflation of "zero" with "unknown" the accrued fee avoids in SQL.
  const [banks, setBanks] = useState<LendingBank[] | null>(null);
  // lendingId whose fee explanation is open (null = closed).
  const [explainId, setExplainId] = useState<number | null>(null);
  const { user } = useAuth();
  const tenantId = user?.currentTenant?.tenantId ?? null;

  // The selector is fed by the banks that actually HAVE lendings, not by the
  // Central bank catalog: a tenant holds lendings at a handful of banks, so the
  // catalog would offer ~20 options that mostly lead to an empty grid.
  useEffect(() => {
    if (tenantId === null) return;
    let cancelled = false;
    setTenantId(tenantId);
    getLendingBanks()
      .then((rows) => {
        if (cancelled) return;
        setBanks(rows);
        // Open on the LARGEST bank rather than on every bank. Not for the
        // database's sake - the view answers all banks in ~50ms - but for the
        // browser's: a tenant can hold tens of thousands of open lendings, and
        // handing all of them to the grid on first paint is what makes the
        // screen feel slow. One bank is also how the work is actually done,
        // since reconciliation against a bank's file runs bank by bank.
        // A bank in the URL (drill-down from Holdings) wins over the default.
        setBankId((cur) => cur ?? (rows.length > 0 ? String(rows[0].bankId) : ''));
      })
      .catch(() => {
        // The list is an optimisation, not a precondition. If it fails, fall back
        // to every bank so the screen still shows its data. banks stays null, so
        // the empty-state banner does NOT appear over that data.
        if (!cancelled) setBankId('');
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Keep-alive internal tab: a second navigation to /lendings?bankId=X&acc=Y re-focuses this
  // already-mounted page, so a CHANGED param after mount must re-apply the filter (same
  // pattern as ?search= in LopCrudPage). Mount-only values were taken in the initial state.
  const lastUrlRef = useRef(`${urlBank}|${urlAcc}`);
  useEffect(() => {
    const key = `${urlBank}|${urlAcc}`;
    if (key === lastUrlRef.current) return;
    lastUrlRef.current = key;
    if (urlBank) setBankId(urlBank);
    setAcc(numOrNull(urlAcc));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlBank, urlAcc]);

  // Accounts of the chosen bank feed the combo; none while no single bank is chosen.
  useEffect(() => {
    if (!bankId) {
      setAccounts([]);
      return;
    }
    let cancelled = false;
    getLendingAccounts(Number(bankId))
      .then((rows) => !cancelled && setAccounts(rows))
      .catch(() => !cancelled && setAccounts([]));
    return () => {
      cancelled = true;
    };
  }, [bankId]);

  const accOptions = useMemo(
    () => accounts.map((a) => ({ id: Number(a.acc), name: a.fundName ?? '' })),
    [accounts],
  );

  // Keyed on bankId + acc: changing the selector gives useCrudGrid a new getRows
  // identity, which re-fetches with the server-side filter.
  //
  // setTenantId first: /api/lendings is tenant-scoped, and on a HARD REFRESH of
  // this page React runs the grid's child effect BEFORE AuthContext's own
  // setTenantId effect, so the first request would go out without X-Tenant-Id
  // and the backend would answer 400 tenant_required. Observed in a browser
  // check, exactly as useFunds documents. Setting it here makes the page
  // self-sufficient regardless of effect ordering.
  const getRows = useCallback(async () => {
    if (tenantId !== null) setTenantId(tenantId);
    // Default not decided yet - do not fetch. Returning empty keeps the grid in
    // its loading state instead of flashing an all-banks result that is about to
    // be replaced.
    if (bankId === null) return [];
    return getLendings(bankId ? Number(bankId) : undefined, acc ?? undefined);
  }, [bankId, acc, tenantId]);

  // Read-only screen. Lendings arrive from the banks' lending files, and
  // dbo.LoadLendings rebuilds every ingested row on each run - an edit made here
  // would simply be overwritten, so there is no write endpoint to call.
  const saveBatch = useCallback(async () => getRows(), [getRows]);

  const columnDefs = useMemo<ColDef<Lending>[]>(
    () => [
      { field: 'bankName', headerName: t('lendings.columns.bankName'), width: 150 },
      { field: 'acc', headerName: t('lendings.columns.acc'), width: 110 },
      { field: 'fundName', headerName: t('lendings.columns.fundName'), width: 220 },
      { field: 'assetId', headerName: t('lendings.columns.assetId'), width: 110 },
      { field: 'assetName', headerName: t('lendings.columns.assetName'), width: 180 },
      { field: 'qty', headerName: t('lendings.columns.qty'), width: 130, valueFormatter: fmtQty, type: 'numericColumn' },
      { field: 'startDate', headerName: t('lendings.columns.startDate'), width: 115, valueFormatter: fmtDate },
      { field: 'endDate', headerName: t('lendings.columns.endDate'), width: 115, valueFormatter: fmtDate },
      { field: 'startRate', headerName: t('lendings.columns.startRate'), width: 110, type: 'numericColumn' },
      { field: 'feePercent', headerName: t('lendings.columns.feePercent'), width: 105, valueFormatter: fmtPercent, type: 'numericColumn' },
      // The number this screen exists for: the fee not yet paid as of the
      // valuation date. Negative = charged up front and not yet earned.
      { field: 'accruedFee', headerName: t('lendings.columns.accruedFee'), width: 130, valueFormatter: fmtMoney, type: 'numericColumn' },
      { field: 'accrualDays', headerName: t('lendings.columns.accrualDays'), width: 100, type: 'numericColumn' },
      // The accrual is read from a table rebuilt at day-open and after every
      // ingestion, so the screen states when it was built rather than letting the
      // number look live.
      { field: 'feeBuiltAt', headerName: t('lendings.columns.feeBuiltAt'), width: 150, valueFormatter: fmtDateTime },
      { field: 'calcTypeName', headerName: t('lendings.columns.calcTypeName'), width: 260 },
      { field: 'calcTypeSource', headerName: t('lendings.columns.calcTypeSource'), width: 110 },
      { field: 'side', headerName: t('lendings.columns.side'), width: 100 },
      { field: 'bankRef', headerName: t('lendings.columns.bankRef'), width: 130 },
      { field: 'asOfDate', headerName: t('lendings.columns.asOfDate'), width: 115, valueFormatter: fmtDate },
    ],
    [],
  );

  // Right-click "הסבר עמלה" - only when an actual row was clicked. Memoized: the
  // context menu's items memo depends on this identity.
  const extraMenuItems = useCallback(({ rowData }: { rowData: Record<string, unknown> | null }) => {
    const id = rowData?.lendingId;
    if (typeof id !== 'number') return [];
    return [
      {
        label: t('lendings.contextMenu.explainFee'),
        icon: <Calculator size={16} />,
        onClick: () => setExplainId(id),
      },
    ];
  }, []);
  const closeExplain = useCallback(() => setExplainId(null), []);

  return (
    <div className="lendings-page">
      <div className="lendings-page__filter">
        <label htmlFor="lendings-bank-filter">{t('lendings.bankLabel')}</label>
        {/* A tenant with no open lendings at all gets told so. Without this the
            selector would offer nothing but "all banks" over an empty grid, which
            reads as a failure rather than as an accurate empty answer. */}
        {banks !== null && banks.length === 0 && (
          <span className="lendings-page__empty">{t('lendings.noBanks')}</span>
        )}
        <select
          id="lendings-bank-filter"
          value={bankId ?? ''}
          disabled={bankId === null}
          hidden={banks !== null && banks.length === 0}
          onChange={(e) => {
            setBankId(e.target.value);
            setAcc(null); // accounts belong to a bank
          }}
        >
          {/* Counts are shown because they are what makes the choice informed -
              they say where the lendings actually are before anything loads. */}
          {(banks ?? []).map((b) => (
            <option key={b.bankId} value={b.bankId}>
              {t('lendings.bankOption', {
                bankName: b.bankName ?? String(b.bankId),
                count: b.lendingCount,
              })}
            </option>
          ))}
          <option value="">{t('lendings.allBanks')}</option>
        </select>
        <label htmlFor="lendings-acc-filter">{t('lendings.accLabel')}</label>
        <div className="lendings-page__acc">
          <LopSearchableSelect
            id="lendings-acc-filter"
            value={acc}
            options={accOptions}
            onChange={setAcc}
            allowNone
            noneLabel={t('lendings.allAccounts')}
            placeholder={t('lendings.allAccounts')}
            disabled={!bankId}
          />
        </div>
      </div>
      <LopCrudPage<Lending>
        resourceCode="lendings"
        titleKey="lendings.pageTitle"
        idField="lendingId"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        showAdd={false}
        showDelete={false}
        extraMenuItems={extraMenuItems}
      />
      {explainId !== null && (
        <LendingFeeExplainModal lendingId={explainId} onClose={closeExplain} />
      )}
    </div>
  );
}
